import { useMemo } from 'react';
import type { PatientRoomResponse } from '../api/generated';
import type { BedContext } from '../components/GanttChart/WeekKpiCell/computeWeekKpi';
import { useAppSelector } from '../store/hooks';
import { selectPatientsById } from '../store/patients/patientsSlice';
import { selectRoomProperties } from '../store/roomProperties/roomPropertiesSlice';

// Joins the location's patient rooms with what the year plan loaded alongside
// them (useYearPlan): the location's patients, whose room needs the bed chip
// judges, and the account's room properties, which name those needs.
// Undefined while the rooms are, so the chip claims nothing without them.
export function useBedContext(
  patientRooms: readonly PatientRoomResponse[] | undefined
): BedContext | undefined {
  const patientsById = useAppSelector(selectPatientsById);
  const roomProperties = useAppSelector(selectRoomProperties);

  return useMemo(() => {
    if (!patientRooms) return undefined;
    // The rooms name the properties they offer; the account's list adds the
    // ones only patients ask for, which no room offers yet.
    const propertyNames = new Map(roomProperties.map((property) => [property.id, property.name]));
    for (const room of patientRooms) {
      for (const property of room.patientRoomProperties) {
        propertyNames.set(property.id, property.name);
      }
    }
    return { rooms: patientRooms, patientsById, propertyNames };
  }, [patientRooms, patientsById, roomProperties]);
}
